import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@src/query';
import { getRemoteConversations, getStoredConversations } from '@src/queries/messaging/api';
import { StoredConversation, EnrichedRightRequest } from 'common';
import { DmConversation } from '@adalong/schemas';

// Extend Window interface for API call tracking and conversation cache
declare global {
  interface Window {
    __lastRemoteApiCall?: number;
    __conversationCache?: {
      conversations: DmConversation[];
      timestamp: number;
      accounts: string[];
    };
  }
}

export interface ConversationData {
  id: string;
  targetId: string;
  interlocutorId: string;
  interlocutorUsername: string;
  lastMessage: string;
  lastActivityDate: string | null;
  hasIPR: boolean;
  iprCount: number;
  ugcCount?: number;
  ugcPreviews?: string[];
  language?: string;
  diffusionCount?: number;
  viewCount?: number;
  enrichedRightRequests?: EnrichedRightRequest[]; // Raw enriched data for advanced processing
  type: 'stored' | 'remote' | 'merged';
  raw: DmConversation | StoredConversation;
  isUnread?: boolean;
  isFavorite?: boolean;
}

export interface ConversationDataManagerState {
  conversations: ConversationData[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  findConversation: (targetId: string, interlocutorId: string) => ConversationData | undefined;
  isOnline: boolean;
  loadMoreStoredConversations: () => Promise<void>;
  hasMoreStoredConversations: boolean;
  isFetchingMoreStoredConversations: boolean;
}

type StoredConversationsPage = Awaited<ReturnType<typeof getStoredConversations>>;

/**
 * Clean, simple conversation data manager
 * - Fetches both stored and remote conversations
 * - Merges them into a unified format
 * - Provides robust error handling and retries
 * - Single source of truth for all conversation data
 */

// Helper functions for processing real conversation data
const findMostFrequentLang = (enrichedRightRequests: EnrichedRightRequest[] = []): string => {
  const langCounts = enrichedRightRequests
    .flatMap((request) => request.medias?.map((media) => media.lang) ?? [])
    .reduce<Record<string, number>>((acc, lang) => {
      if (typeof lang === 'string') {
        acc[lang] = (acc[lang] || 0) + 1;
      }
      return acc;
    }, {});

  return Object.keys(langCounts).reduce((a, b) => (langCounts[a] > langCounts[b] ? a : b), 'N/A');
};

const countMediasInDiffusion = (enrichedRightRequests: EnrichedRightRequest[] = []): number => {
  return enrichedRightRequests.reduce(
    (acc, request) => acc + (request.medias?.filter((media) => media.diffusion).length ?? 0),
    0,
  );
};

const extractUGCThumbnails = (enrichedRightRequests: EnrichedRightRequest[] = []): string[] => {
  return enrichedRightRequests
    .flatMap((request) => request.medias?.map((media) => media.url.thumbnail) ?? [])
    .filter((thumbnail): thumbnail is string => typeof thumbnail === 'string')
    .slice(0, 3); // Limit to 3 thumbnails
};

const countUGCMedias = (enrichedRightRequests: EnrichedRightRequest[] = []): number => {
  return enrichedRightRequests.flatMap((request) => request.medias ?? []).length;
};

const getTimestampFromDateString = (value?: string | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

export function useConversationDataManager(): ConversationDataManagerState {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [hasInitialData, setHasInitialData] = useState(false);

  // Track online/offline status of browser
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch stored conversations (Rights Request data)
  const {
    data: storedPages,
    isLoading: isLoadingStored,
    error: storedError,
    refetch: refetchStored,
    fetchNextPage: fetchNextStoredPage,
    hasNextPage: hasMoreStoredPages,
    isFetchingNextPage: isFetchingMoreStored,
  } = useInfiniteQuery<StoredConversationsPage>({
    queryKey: QUERY_KEYS.conversations(),
    queryFn: ({ pageParam }) => getStoredConversations(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    enabled: isOnline,
    getNextPageParam: (lastPage) => lastPage?.cursor || undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Fetch remote conversations (live Instagram data)
  const {
    data: remoteData,
    isLoading: isLoadingRemote,
    error: remoteError,
    refetch: refetchRemote,
  } = useQuery({
    queryKey: QUERY_KEYS.remoteConversations(),
    queryFn: () => {
      const callStartTime = new Date();
      window.__lastRemoteApiCall = callStartTime.getTime();
      return getRemoteConversations();
    },
    enabled: isOnline,
    staleTime: 30 * 1000, // 30 seconds (live data needs frequent updates)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: isOnline ? 60 * 1000 : false, // Auto-refresh every minute when online
  });

  // React to errors from stored query (was onError callback)
  useEffect(() => {
    if (storedError) {
      setGlobalError(`Failed to load conversations: ${(storedError as any).message}`);
    }
  }, [storedError]);

  // React to errors and settled state from remote query (was onError + onSettled callbacks)
  useEffect(() => {
    if (remoteError) {
      setGlobalError(`Failed to load Instagram conversations: ${(remoteError as any).message}`);
    }
  }, [remoteError]);

  useEffect(() => {
    if (!isLoadingRemote) {
      setHasInitialData(true);
    }
  }, [isLoadingRemote]);

  // Cache remote data on success (was onSuccess callback)
  useEffect(() => {
    if (!remoteData) return;
    const accounts = [
      ...new Set(
        remoteData?.conversations?.map((conv: DmConversation) => conv.conversationTarget.id) || [],
      ),
    ] as string[];

    /**Due to the instability of the instagram API, we're going to cache data
     * we already received in order to not "lose" data during a session */

    // Track all unique accounts we've ever seen
    const allKnownAccounts = new Set(
      JSON.parse(localStorage.getItem('adalong_known_accounts') || '[]') as string[],
    );
    accounts.forEach((id) => allKnownAccounts.add(id));
    localStorage.setItem('adalong_known_accounts', JSON.stringify([...allKnownAccounts]));

    // Smart caching: Only update cache if we get equal or more accounts than before
    const currentCache = window.__conversationCache;
    const shouldUpdateCache = !currentCache || accounts.length >= currentCache.accounts.length;

    if (shouldUpdateCache) {
      window.__conversationCache = {
        conversations: remoteData?.conversations || [],
        timestamp: Date.now(),
        accounts: accounts,
      };
    }
  }, [remoteData]);

  // Process and normalize stored conversations
  const flattenedStoredConversations = useMemo(() => {
    return storedPages?.pages.flatMap((page) => page?.conversations ?? []) ?? [];
  }, [storedPages]);

  const normalizedStoredConversations = useMemo(() => {
    if (!flattenedStoredConversations.length) {
      return [];
    }

    const normalized = flattenedStoredConversations.map(
      (stored: StoredConversation): ConversationData => {
        // Extract real data from enriched right requests
        const enrichedRightRequests = stored.rightRequests || [];
        const ugcThumbnails = extractUGCThumbnails(enrichedRightRequests);
        const ugcCount = countUGCMedias(enrichedRightRequests);
        const language = findMostFrequentLang(enrichedRightRequests);
        const diffusionCount = countMediasInDiffusion(enrichedRightRequests);

        const firstRightRequest = stored.rightRequests?.[0]?.rightRequest;
        const lastActivityDate = stored.lastActivityDate
          ? stored.lastActivityDate
          : firstRightRequest?.createdAt
            ? firstRightRequest.createdAt.toISOString()
            : null;

        const conv = {
          id: stored.conversationId,
          targetId: stored.targetAccountId,
          interlocutorId: stored.interlocutorId,
          interlocutorUsername: stored.interlocutorUsername,
          lastMessage: `${stored.rightRequests?.length || 0} rights agreed`,
          lastActivityDate,
          hasIPR: true,
          iprCount: stored.rightRequests?.length || 0,
          ugcCount: ugcCount,
          ugcPreviews: ugcThumbnails,
          language: language,
          diffusionCount: diffusionCount,
          viewCount: undefined, // Not available in stored conversations
          enrichedRightRequests: enrichedRightRequests, // Pass raw enriched data
          type: 'stored' as const,
          raw: stored,
          isUnread: stored.isUnread,
          isFavorite: stored.isFavorite
        };

        return conv;
      },
    );

    return normalized;
  }, [flattenedStoredConversations]);

  // Process and normalize remote conversations with smart fallback
  const normalizedRemoteConversations = useMemo(() => {
    // Check if we should use cached data instead of current API response
    const currentConversations = remoteData?.conversations || [];
    const cache = window.__conversationCache;
    const allKnownAccounts = JSON.parse(
      localStorage.getItem('adalong_known_accounts') || '[]',
    ) as string[];

    let dataToUse = currentConversations;
    let dataSource = 'API';

    // Use cache if current data is missing known accounts and cache is recent (< 5 minutes)
    if (cache && allKnownAccounts.length > 0) {
      const currentAccounts = [
        ...new Set(currentConversations.map((conv: DmConversation) => conv.conversationTarget.id)),
      ] as string[];
      const isCacheRecent = Date.now() - cache.timestamp < 5 * 60 * 1000; // 5 minutes
      const isCurrentDataIncomplete = currentAccounts.length < allKnownAccounts.length;
      const isCacheBetter = cache.accounts.length > currentAccounts.length;

      if (isCacheRecent && isCurrentDataIncomplete && isCacheBetter) {
        dataToUse = cache.conversations;
        dataSource = 'CACHE (API unstable)';
      }
    }

    if (!dataToUse || dataToUse.length === 0) {
      return [];
    }

    const normalized = dataToUse.map((remote: DmConversation): ConversationData => {
      // Remote conversations don't have Rights Request data, so no UGC previews or diffusion data
      const conv = {
        id: remote.id,
        targetId: remote.conversationTarget.id,
        interlocutorId: remote.conversationInterlocutor.id,
        interlocutorUsername: remote.conversationInterlocutor.name,
        lastMessage: remote.lastMessage || 'Content not available',
        lastActivityDate: remote.lastActivityDate,
        hasIPR: false,
        iprCount: 0,
        ugcCount: 0, // No UGC data in remote conversations
        ugcPreviews: [], // No UGC previews in remote conversations
        language: 'N/A', // Language detection requires Rights Request data
        diffusionCount: 0, // No diffusion data in remote conversations
        viewCount: undefined, // Not available in remote conversations
        enrichedRightRequests: [], // No enriched data in remote conversations
        isUnread: true, // No unread status in remote conversations
        isFavorite: false, // No favorites in remote conversations
        type: 'remote' as const,
        raw: remote
      };

      return conv;
    });

    return normalized;
  }, [remoteData]);

  // Merge stored and remote conversations intelligently
  const mergedConversations = useMemo(() => {
    const conversationMap = new Map<string, ConversationData>();

    // Add all remote conversations first (live data)
    normalizedRemoteConversations.forEach((remote: ConversationData) => {
      const key = `${remote.targetId}-${remote.interlocutorId}`;
      conversationMap.set(key, remote);
    });

    // Merge stored conversations (Rights Request data)
    normalizedStoredConversations.forEach((stored: ConversationData) => {
      const key = `${stored.targetId}-${stored.interlocutorId}`;
      const existing = conversationMap.get(key);

      if (existing) {
        // Merge: combine live data with Rights Request data
        // When merging, the stored data (with Rights Request) takes precedence for UGC/diffusion data
        conversationMap.set(key, {
          ...existing, // Keep live message data
          hasIPR: true,
          iprCount: stored.iprCount,
          type: 'merged',
          lastMessage: existing.lastMessage, // Prefer live message over Rights Request count
          lastActivityDate: existing.lastActivityDate ?? stored.lastActivityDate,
          // Use Rights Request data for UGC, language, and diffusion (from stored conversation)
          ugcCount: stored.ugcCount,
          ugcPreviews: stored.ugcPreviews,
          language: stored.language,
          diffusionCount: stored.diffusionCount,
          enrichedRightRequests: stored.enrichedRightRequests,
          isUnread: stored.isUnread,
          isFavorite: stored.isFavorite,
          // Keep remote data fields that aren't in stored
          viewCount: existing.viewCount,
        });
      } else {
        // Add Rights Request-only conversation (might not have recent Instagram activity)
        conversationMap.set(key, {
          ...stored,
          lastMessage: `${stored.iprCount} rights agreed (no recent activity)`,
        });
      }
    });

    const getConversationTimestamp = (conv: ConversationData) => {
      const time = getTimestampFromDateString(conv.lastActivityDate);
      return time ?? Number.NEGATIVE_INFINITY;
    };

    const finalConversations = Array.from(conversationMap.values()).sort((a, b) => {
      // Sort by last activity, most recent first (unknown dates go last)
      return getConversationTimestamp(b) - getConversationTimestamp(a);
    });

    return finalConversations;
  }, [normalizedStoredConversations, normalizedRemoteConversations]);

  // Find conversation by target and interlocutor
  const findConversation = useCallback(
    (targetId: string, interlocutorId: string): ConversationData | undefined => {
      return mergedConversations.find(
        (conv) => conv.targetId === targetId && conv.interlocutorId === interlocutorId,
      );
    },
    [mergedConversations],
  );

  // Global refetch function
  const refetch = useCallback(async () => {
    setGlobalError(null);
    try {
      await Promise.all([refetchStored(), refetchRemote()]);
    } catch (error: any) {
      setGlobalError(`Failed to refresh conversations: ${error.message}`);
    }
  }, [refetchStored, refetchRemote]);

  const loadMoreStoredConversations = useCallback(async () => {
    if (!hasMoreStoredPages || isFetchingMoreStored || !isOnline) {
      return;
    }
    try {
      await fetchNextStoredPage();
    } catch (error: any) {
      setGlobalError(`Failed to load more conversations: ${error.message}`);
    }
  }, [fetchNextStoredPage, hasMoreStoredPages, isFetchingMoreStored, isOnline]);

  // Clear errors when coming back online
  useEffect(() => {
    if (isOnline && globalError) {
      setGlobalError(null);
    }
  }, [isOnline, globalError]);

  // Determine overall error state
  const error = useMemo(() => {
    if (!isOnline) return 'No internet connection';
    if (globalError) return globalError;
    if (storedError && remoteError) return 'Failed to load conversation data';
    if (storedError) return 'Failed to load Rights Request data';
    if (remoteError) return 'Failed to load Instagram data';
    return null;
  }, [isOnline, globalError, storedError, remoteError]);

  return {
    conversations: mergedConversations,
    isLoading: (isLoadingStored || isLoadingRemote) && !hasInitialData, // Only show loading if we haven't completed initial data fetch
    error,
    refetch,
    findConversation,
    isOnline,
    loadMoreStoredConversations,
    hasMoreStoredConversations: Boolean(hasMoreStoredPages),
    isFetchingMoreStoredConversations: isFetchingMoreStored,
  };
}

/**
 * Utility function to create conversation unique identifier
 */
export function createConversationKey(targetId: string, interlocutorId: string): string {
  return `${targetId}-${interlocutorId}`;
}
